import {ValueObject} from '@s-ui/domain'

export class PriceDropValueObject extends ValueObject {
  #originalPrice
  #percentage
  #daysSinceUpdate

  static ONE_DAY_IN_MS = 24 * 60 * 60 * 1000
  static PRICE_DROP_MIN_VALID_PERCENTAGE = 3
  static PRICE_DROP_MAX_VALID_PERCENTAGE = 40
  static PRICE_DROP_VALID_DAYS = 7

  constructor({originalPrice, percentage, daysSinceUpdate}) {
    super()
    this.#originalPrice = originalPrice
    this.#percentage = percentage
    this.#daysSinceUpdate = daysSinceUpdate
  }

  static getDaysUntilToday(dateInThePast) {
    return Math.round((new Date().getTime() - new Date(dateInThePast).getTime()) / PriceDropValueObject.ONE_DAY_IN_MS)
  }

  /**
   * Creates a PriceDropValueObject from API data
   * Returns undefined if price drop data is invalid or doesn't meet criteria
   * @param {Object} params - API data parameters
   * @param {Object} params.price - Price object from API
   * @param {number} params.price.amount - Current price amount (from search results)
   * @param {number} params.price.price - Current price (from ad details)
   * @param {Object} params.price.priceDropData - Price drop data from API
   * @param {number} params.price.priceDropData.amountFromOriginal - Amount difference from original
   * @param {number} params.price.priceDropData.percentageFromOriginal - Percentage difference from original
   * @param {string} params.price.priceDropData.date - Date of price drop
   * @returns {PriceDropValueObject|undefined} - PriceDropValueObject instance or undefined
   */
  static createFromApi({price}) {
    if (!price) return

    const {priceDropData} = price

    const hasPriceDrop = Boolean(
      priceDropData?.amountFromOriginal && priceDropData?.percentageFromOriginal && priceDropData?.date
    )

    if (!hasPriceDrop) return

    const daysSinceUpdate = PriceDropValueObject.getDaysUntilToday(priceDropData.date)
    const percentage = priceDropData.percentageFromOriginal

    if (
      percentage < PriceDropValueObject.PRICE_DROP_MIN_VALID_PERCENTAGE ||
      percentage > PriceDropValueObject.PRICE_DROP_MAX_VALID_PERCENTAGE ||
      daysSinceUpdate >= PriceDropValueObject.PRICE_DROP_VALID_DAYS
    ) {
      return
    }

    // Calculate original price - handle both price.amount (search results) and price.price (ad details)
    const currentPrice = price.amount ?? price.price
    const originalPrice = currentPrice + priceDropData.amountFromOriginal

    return PriceDropValueObject.create({
      originalPrice,
      percentage,
      daysSinceUpdate
    })
  }

  /**
   * Creates a PriceDropValueObject instance
   * @param {Object} params - Creation parameters
   * @param {number} params.originalPrice - Original price before drop
   * @param {number} params.percentage - Percentage of price drop
   * @param {number} params.daysSinceUpdate - Days since price was updated
   * @returns {PriceDropValueObject} - PriceDropValueObject instance
   */
  static create({originalPrice, percentage, daysSinceUpdate}) {
    return new PriceDropValueObject({
      originalPrice,
      percentage,
      daysSinceUpdate
    })
  }

  get originalPrice() {
    return this.#originalPrice
  }

  get percentage() {
    return this.#percentage
  }

  get daysSinceUpdate() {
    return this.#daysSinceUpdate
  }

  get value() {
    return {
      originalPrice: this.#originalPrice,
      percentage: this.#percentage,
      daysSinceUpdate: this.#daysSinceUpdate
    }
  }
}
